import { ChevronDown, FileText, PlayCircle } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';

import { useCan } from '@/auth/queries';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ImagenProtegida } from '@/components/ContenidoProtegido';
import { AVATARES } from '@/lib/avatares';
import { useStagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

import {
  Cargando,
  ErrorCarga,
  FiltroEspecialidad,
  TabsModulo,
  VisorLaminas,
} from '../curso/componentes';
import {
  useFlashcards,
  useGuiasGpc,
  usePresentaciones,
  useResumenesGpc,
  useVideotips,
} from '../curso/queries';

interface Agrupado<T> {
  specialties: { specialty: string; groups: { name: string; items: T[] }[] }[];
}

// Las tres primeras llevan la foto de su tarjeta en el legacy; el resto, su ícono.
const SECCIONES: {
  ruta: string;
  titulo: string;
  texto: string;
  icono: string;
  foto?: string;
  permiso?: string;
}[] = [
  {
    ruta: 'presentaciones',
    titulo: 'Presentaciones',
    texto: 'Diapositivas de cada tema para repasar a tu ritmo.',
    icono: '/img/iconos/icono-biblioteca.png',
    foto: '/img/biblioteca/presentaciones.jpg',
  },
  {
    ruta: 'flashcards',
    titulo: 'Flash cards',
    texto: 'Láminas con los datos clave de cada especialidad.',
    icono: '/img/iconos/categoria.png',
    foto: '/img/biblioteca/flashcards.jpg',
  },
  {
    ruta: 'flashcards-evaluadas',
    titulo: 'Evaluadas en el ENARM',
    texto: 'Las flashcards de temas que ya se han preguntado.',
    icono: '/img/iconos/enarm.png',
  },
  {
    ruta: 'video-tips',
    titulo: 'Video tips',
    texto: 'Videos cortos con lo esencial de cada tema.',
    icono: '/img/iconos/modo.png',
    foto: '/img/biblioteca/videos.jpg',
  },
  {
    ruta: 'guias-gpc',
    titulo: 'Guías GPC',
    texto: 'Guías de práctica clínica de las clases.',
    icono: '/img/iconos/icono-temario.png',
    permiso: 'class.gpc_guides.access',
  },
  {
    ruta: 'resumenes-gpc',
    titulo: 'Resúmenes de guías GPC',
    texto: 'Lo más importante de cada guía, por bloques.',
    icono: '/img/iconos/icono-examenes.png',
    permiso: 'library.gpc_summaries.access',
  },
];

/** Tarjeta horizontal del hub legacy: foto a la izquierda (crece al pasar) + rótulo azul. */
function Seccion({ s }: { s: (typeof SECCIONES)[number] }) {
  const puede = useCan(s.permiso);
  if (puede === false) return null;
  return (
    <Link
      data-card
      to={`/curso-enarm/biblioteca/${s.ruta}`}
      className="group card-vidrio card-vidrio-hover flex min-h-28 overflow-hidden border-2 border-azul-600 p-0 no-underline"
    >
      {s.foto ? (
        <img
          src={s.foto}
          alt=""
          className="w-2/5 shrink-0 object-cover transition-[width] duration-200 group-hover:w-[45%]"
        />
      ) : (
        <span className="grid w-2/5 shrink-0 place-items-center bg-marca transition-[width] duration-200 group-hover:w-[45%]">
          <img src={s.icono} alt="" className="h-14 w-14 object-contain brightness-0 invert" />
        </span>
      )}
      <span className="flex flex-col justify-center px-4 py-3">
        <span className="block text-lg leading-tight font-extrabold text-azul-texto uppercase">
          {s.titulo}
        </span>
        <span className="mt-1 text-xs text-texto-2">{s.texto}</span>
      </span>
    </Link>
  );
}

/** Hub de la biblioteca (skill §7). */
export function BibliotecaPage() {
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-card]', { step: 60 });
  return (
    <div ref={root} className="space-y-6">
      <TabsModulo />
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Biblioteca</span>
      </h1>
      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {SECCIONES.map((s) => (
          <Seccion key={s.ruta} s={s} />
        ))}
      </div>
    </div>
  );
}

/**
 * Listado agrupado por especialidad → subespecialidad con filtro (skill §7). La especialidad
 * elegida vive en `?especialidad=`.
 */
function Listado<T>({
  titulo,
  instruccion = 'Selecciona una especialidad para desplegar su contenido.',
  avatar = AVATARES.doctorSenala,
  data,
  error,
  isPending,
  vacio,
  extra,
  render,
  rejilla = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
}: {
  titulo: string;
  instruccion?: string;
  avatar?: string;
  data: Agrupado<T> | undefined;
  error: unknown;
  isPending: boolean;
  vacio: string;
  extra?: ReactNode;
  render: (item: T) => ReactNode;
  rejilla?: string;
}) {
  const [params, setParams] = useSearchParams();
  const elegida = params.get('especialidad');
  const root = useRef<HTMLDivElement>(null);
  const opciones = useMemo(
    () =>
      (data?.specialties ?? []).map((s) => ({
        nombre: s.specialty,
        conteo: s.groups.reduce((n, g) => n + g.items.length, 0),
      })),
    [data],
  );
  const visibles = (data?.specialties ?? []).filter((s) => !elegida || s.specialty === elegida);
  useStagger(root, '[data-card]', { step: 40, deps: [elegida, data] });

  return (
    <div className="space-y-6">
      <TabsModulo />
      {/* Rótulo verde del legacy (todas las secciones de la biblioteca lo usan). */}
      <div className="text-center">
        <h1 className="rotulo-pill rotulo-pill-verde">{titulo}</h1>
        <p className="mt-3 text-sm font-semibold text-texto-2">{instruccion}</p>
      </div>
      {extra}
      {isPending && <Cargando />}
      {error ? <ErrorCarga error={error} /> : null}
      {data && opciones.length === 0 && (
        <p className="panel-vidrio text-center text-texto-2">{vacio}</p>
      )}
      {opciones.length > 0 && (
        <div className="flex items-end justify-center gap-3">
          <img src={avatar} alt="" className="hidden h-16 w-auto shrink-0 sm:block" />
          <FiltroEspecialidad
            opciones={opciones}
            valor={elegida}
            onChange={(v) => {
              setParams(v ? { especialidad: v } : {}, { replace: true });
            }}
          />
        </div>
      )}
      <div ref={root} className="space-y-8">
        {visibles.map((s) => (
          <section key={s.specialty} className="space-y-4">
            <h2 className="text-xl font-extrabold text-azul-texto uppercase">
              {s.specialty}
              <span className="mt-1 block h-[3px] w-16 rounded-full bg-verde-500" />
            </h2>
            {s.groups.map((g) => (
              <div key={g.name} className="space-y-3">
                {s.groups.length > 1 || g.name !== s.specialty ? (
                  <h3 className="text-sm font-extrabold tracking-wide text-verde-texto uppercase">
                    {g.name}
                  </h3>
                ) : null}
                <div className={cn('grid gap-4', rejilla)}>{g.items.map(render)}</div>
              </div>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

export function PresentacionesPage() {
  const [licencia, setLicencia] = useState<number | undefined>();
  const q = usePresentaciones(licencia);
  const filtros = q.data?.licenseFilters ?? [];
  return (
    <Listado
      titulo="Presentaciones"
      instruccion="Selecciona una especialidad para desplegar las presentaciones disponibles."
      avatar={AVATARES.doctorSenala}
      {...q}
      data={q.data}
      vacio="Tu licencia actual no incluye presentaciones."
      extra={
        filtros.length > 1 ? (
          <div role="group" aria-label="Licencia" className="flex flex-wrap justify-center gap-2">
            {[{ membershipId: undefined, name: 'Todas mis licencias' }, ...filtros].map((f) => (
              <button
                key={f.membershipId ?? 0}
                type="button"
                className="btn-modulo text-xs"
                data-state={licencia === f.membershipId ? 'active' : undefined}
                aria-pressed={licencia === f.membershipId}
                onClick={() => {
                  setLicencia(f.membershipId);
                }}
              >
                {f.name}
              </button>
            ))}
          </div>
        ) : null
      }
      render={(p) => (
        <Link
          key={p.libraryId}
          data-card
          to={`/curso-enarm/biblioteca/presentaciones/${String(p.libraryId)}`}
          className="card-vidrio card-vidrio-hover overflow-hidden p-0"
        >
          {p.coverUrl ? (
            <ImagenProtegida
              src={p.coverUrl}
              clave={p.libraryId}
              alt=""
              className="aspect-video object-cover"
              onExpirada={() => q.refetch({ cancelRefetch: false })}
            />
          ) : (
            <div className="aspect-video bg-white/5" />
          )}
          <p className="p-3 text-sm font-bold">{p.name}</p>
        </Link>
      )}
    />
  );
}

export function FlashcardsPage() {
  const q = useFlashcards(false);
  return (
    <Listado
      titulo="Flash cards"
      instruccion="Selecciona una especialidad para desplegar las flashcards disponibles."
      avatar={AVATARES.doctoraSenala}
      {...q}
      data={q.data}
      vacio="Tu licencia actual no incluye flashcards."
      rejilla="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
      render={(f) => (
        <Link
          key={f.flashcardId}
          data-card
          to={`/curso-enarm/biblioteca/flashcards/${String(f.flashcardId)}`}
          className="card-vidrio card-vidrio-hover flex items-center gap-3 p-4"
        >
          <img src="/img/iconos/categoria.png" alt="" className="h-9 w-9 object-contain" />
          <span className="text-sm font-bold">{f.name}</span>
        </Link>
      )}
    />
  );
}

/** Evaluadas en el ENARM: rejilla de láminas; al abrir una, visor con anterior/siguiente. */
export function FlashcardsEvaluadasPage() {
  const q = useFlashcards(true);
  const [abierta, setAbierta] = useState<number | null>(null);
  const todas = (q.data?.specialties ?? []).flatMap((s) => s.groups.flatMap((g) => g.items));
  const actual = todas.findIndex((f) => f.flashcardId === abierta);
  return (
    <>
      <Listado
        titulo="Evaluadas en el ENARM"
        avatar={AVATARES.doctoraSenala}
        {...q}
        data={q.data}
        vacio="Aún no hay flashcards evaluadas para tu licencia."
        rejilla="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
        render={(f) => (
          <button
            key={f.flashcardId}
            data-card
            type="button"
            className="card-vidrio card-vidrio-hover overflow-hidden p-0 text-left"
            onClick={() => {
              setAbierta(f.flashcardId);
            }}
          >
            <p className="px-3 pt-3 text-xs font-extrabold text-azul-texto uppercase">{f.name}</p>
            {f.imageUrl && (
              <ImagenProtegida
                src={f.imageUrl}
                clave={f.flashcardId}
                alt=""
                className="p-3"
                onExpirada={() => q.refetch({ cancelRefetch: false })}
              />
            )}
          </button>
        )}
      />
      <Dialog
        open={actual >= 0}
        onOpenChange={(o) => {
          if (!o) setAbierta(null);
        }}
      >
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto">
          <DialogTitle>Evaluadas en el ENARM</DialogTitle>
          {actual >= 0 && (
            <div className="mt-4">
              <VisorLaminas
                titulo="Flashcard evaluada"
                contenido={['flashcard', todas[actual]?.flashcardId ?? 0]}
                inicial={actual + 1}
                laminas={todas.map((f) => ({
                  clave: f.flashcardId,
                  imageUrl: f.imageUrl ?? '',
                  nombre: f.name,
                }))}
                onExpirada={() => q.refetch()}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function VideoTipsPage() {
  const q = useVideotips();
  return (
    <Listado
      titulo="Video tips"
      instruccion="Selecciona una especialidad para desplegar los videos disponibles."
      {...q}
      data={q.data}
      vacio="Tu licencia actual no incluye video tips."
      render={(v) => (
        <Link
          key={v.libraryId}
          data-card
          to={`/curso-enarm/biblioteca/video-tips/${String(v.libraryId)}`}
          className="card-vidrio card-vidrio-hover flex items-center gap-3 p-4"
        >
          <PlayCircle className="shrink-0 text-cian-texto" size={30} />
          <span className="text-sm font-bold">{v.name}</span>
        </Link>
      )}
    />
  );
}

/** Guías GPC: tarjeta con muesca de cerebro y acordeón por clase (skill §7). */
export function GuiasGpcPage() {
  const q = useGuiasGpc();
  const [abierta, setAbierta] = useState<number | null>(null);
  const imagenes = (q.data?.specialties ?? []).flatMap((s) =>
    s.groups.flatMap((g) => g.items.filter((i) => i.kind === 'image')),
  );
  const actual = imagenes.findIndex((i) => i.id === abierta);
  return (
    <>
      <Listado
        titulo="Guías GPC"
        {...q}
        data={q.data}
        vacio="Aún no hay guías disponibles."
        rejilla="grid-cols-1 md:grid-cols-2"
        render={(g) => (
          <button
            key={g.id}
            data-card
            type="button"
            className="card-vidrio card-vidrio-hover flex items-center gap-3 p-4 text-left"
            onClick={() => {
              if (g.kind === 'pdf') window.open(g.fileUrl, '_blank', 'noopener');
              else setAbierta(g.id);
            }}
          >
            <FileText className="shrink-0 text-verde-texto" />
            <span>
              <span className="block text-sm font-bold">{g.name}</span>
              <span className="text-xs text-texto-3">{g.className}</span>
            </span>
            <ChevronDown className="ml-auto -rotate-90 text-texto-3" size={16} />
          </button>
        )}
      />
      <Dialog
        open={actual >= 0}
        onOpenChange={(o) => {
          if (!o) setAbierta(null);
        }}
      >
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto">
          <DialogTitle>{imagenes[actual]?.name ?? 'Guía GPC'}</DialogTitle>
          {actual >= 0 && (
            <div className="mt-4">
              <VisorLaminas
                titulo="Guía GPC"
                contenido={['gpc_guide', imagenes[actual]?.id ?? 0]}
                inicial={actual + 1}
                laminas={imagenes.map((g) => ({
                  clave: g.id,
                  imageUrl: g.fileUrl,
                  nombre: g.name,
                }))}
                onExpirada={() => q.refetch()}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ResumenesGpcPage() {
  const q = useResumenesGpc();
  return (
    <Listado
      titulo="Resúmenes de guías GPC"
      avatar={AVATARES.doctoraSenala}
      {...q}
      data={q.data}
      vacio="Aún no hay resúmenes disponibles para tu licencia."
      rejilla="grid-cols-1 md:grid-cols-2"
      render={(r) => (
        <Link
          key={r.libraryId}
          data-card
          to={`/curso-enarm/biblioteca/resumenes-gpc/${String(r.libraryId)}`}
          className="card-vidrio card-vidrio-hover flex items-center gap-3 p-4"
        >
          <FileText className="shrink-0 text-verde-texto" />
          <span className="text-sm font-bold">{r.name}</span>
          {r.ultra && <span className="insignia insignia-activa ml-auto">Ultra</span>}
        </Link>
      )}
    />
  );
}
